import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardHeader,
    Container,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    Button,
    TextField
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { saveAs } from 'file-saver';

function SalesReport() {
    const [salesData, setSalesData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [username, setUsername] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    useEffect(() => {
        fetchSalesData();
    }, []);

    useEffect(() => {
        filterSalesData();
    }, [salesData, username, fromDate, toDate]);

    async function fetchSalesData() {
        try {
            const res = await axios.get('https://serveralgama.mylksystems.com/sales/getdailysales');
            if (res.data.length > 0) {
                setSalesData(res.data);
                setFilteredData(res.data);
            } else {
                toast.error("No sales records found!");
            }
        } catch (error) {
            console.error("Error fetching sales data", error);
        }
    }

    function filterSalesData() {
        let filtered = salesData;

        if (username) {
            filtered = filtered.filter(sale => sale.username.toLowerCase().includes(username.toLowerCase()));
        }

        if (fromDate) {
            filtered = filtered.filter(sale => new Date(sale.inv_date) >= new Date(fromDate));
        }

        if (toDate) {
            filtered = filtered.filter(sale => new Date(sale.inv_date) <= new Date(toDate));
        }

        setFilteredData(filtered);
    }

    function downloadCSV(fileName, data) {
        let csvContent =
            "Date,Username,Total Sales\n" +
            data.map(sale => `${sale.inv_date},${sale.username},${sale.Total}`).join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, fileName);
    }

    // Group sales data by date and username
    const groupedData = {};
    filteredData.forEach(({ inv_date, username, Total }) => {
        if (!groupedData[inv_date]) {
            groupedData[inv_date] = {};
        }
        groupedData[inv_date][username] = parseFloat(Total);
    });

    // Extract unique usernames (for clustered bars)
    const uniqueUsernames = [...new Set(filteredData.map(sale => sale.username))];

    // Convert grouped data to array format for Recharts
    const chartData = Object.keys(groupedData).map(date => {
        let obj = { date };
        uniqueUsernames.forEach(user => {
            obj[user] = groupedData[date][user] || 0;
        });
        return obj;
    });

    // Compute total daily sales
    const dailySales = {};
    filteredData.forEach(({ inv_date, Total }) => {
        if (!dailySales[inv_date]) {
            dailySales[inv_date] = 0;
        }
        dailySales[inv_date] += parseFloat(Total);
    });

    // Convert to array format for bar chart & table
    const dailySalesData = Object.keys(dailySales).map(date => ({
        date,
        totalSales: dailySales[date]
    }));

    // Color palette for users
    const colors = ["#3f51b5", "#ff5722", "#4caf50", "#fbc02d", "#9c27b0"];

    return (
        <Container maxWidth={false}>
            <Card sx={{ padding: '15px' }}>
                <CardHeader title="User-wise Sales Report" />
                <Divider />
                <Box display="flex" gap={2} mt={2} mb={2}>
                    <TextField
                        label="Search Username"
                        variant="outlined"
                        size="small"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                    />
                    <TextField
                        label="From Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                    />
                    <TextField
                        label="To Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        variant="outlined"
                        size="small"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                    />
                    <Button variant="contained" color="primary" onClick={() => downloadCSV("sales_report.csv", filteredData)}>
                        Download CSV
                    </Button>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Sales (Rs.)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{row.inv_date}</TableCell>
                                    <TableCell>{row.username}</TableCell>
                                    <TableCell>{parseFloat(row.Total).toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[5, 10, 25]}
                                    count={filteredData.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={(event, newPage) => setPage(newPage)}
                                    onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
                <Divider />
                
                {/* Clustered Bar Chart */}
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData}>
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        {uniqueUsernames.map((user, index) => (
                            <Bar key={user} dataKey={user} fill={colors[index % colors.length]} />
                        ))}
                    </BarChart>
                </ResponsiveContainer>

                <Divider sx={{ my: 3 }} />

                {/* Daily Total Sales Bar Chart */}
                <CardHeader title="Daily Combined Sales" />
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailySalesData}>
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="totalSales" fill="#1976d2" />
                    </BarChart>
                </ResponsiveContainer>

                {/* Daily Sales Table */}
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total Sales (Rs.)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dailySalesData.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{row.date}</TableCell>
                                    <TableCell>{row.totalSales.toFixed(2)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box mt={2}>
                    <Button variant="contained" color="primary" onClick={() => downloadCSV("daily_sales_report.csv", dailySalesData)}>
                        Download Daily Sales CSV
                    </Button>
                </Box>
            </Card>
        </Container>
    );
}

export default SalesReport;
